import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { type ReactNode } from 'react';

import { PageHeaderBreadcrumb } from '../PageHeader/types';
import { SubPageBreadcrumbHeader } from '../SubPageBreadcrumbHeader/SubPageBreadcrumbHeader';

/**
 * One entry in the condensed meta row under a detail-page title — e.g.
 * `Σ Type: Analytical variable`, `👤 Owner: …`, a dataset link, or a
 * status chip. Kept deliberately open so each domain supplies its own icon
 * vocabulary (MUI icon, custom `Icon`, or a `StatusDisplay`) and value
 * shape (plain text, a link, etc.).
 */
export interface ProjectDetailHeaderMetaItem {
  /** Stable key for the item (also used as a testid suffix). */
  key: string;
  /** Optional leading icon element (MUI icon or the custom `Icon`). */
  icon?: ReactNode;
  /** Optional label rendered before the value, e.g. `Owner:`. */
  label?: string;
  /**
   * The value — a string (rendered as secondary body2 text), or any node
   * (a `Link`, a `StatusDisplay`, a skeleton while loading, …).
   */
  value: ReactNode;
}

export interface ProjectDetailHeaderProps {
  /** Stable id prefix for testids (shared with the breadcrumb header). */
  id: string;
  /**
   * Breadcrumb trail; leading crumb is the project, last crumb is the page.
   * Omit (or pass empty) for hosts without a breadcrumb — e.g. a modal, or a
   * flag-off route — in which case only the title + meta render.
   */
  breadcrumbs?: PageHeaderBreadcrumb[];
  /** Big page title rendered under the breadcrumb. */
  title?: string;
  /** When true, the title renders as a skeleton (name still loading). */
  isTitleLoading?: boolean;
  /** Slot rendered in place of the plain title (inline-edit, badges, …). */
  titleSlot?: ReactNode;
  /** Right-aligned actions on the title row (chips, buttons). */
  rightContent?: ReactNode;
  /**
   * Single line between the title and the meta row, for the one fact that
   * qualifies the whole artifact rather than describing it — the dataset it was
   * built on. Kept out of the meta row deliberately: the meta row is a set of
   * peer attributes, and this reads as part of the artifact's identity.
   */
  subtitle?: ReactNode;
  /** Condensed meta row beneath the title. Omit/empty to hide it. */
  metaItems?: ProjectDetailHeaderMetaItem[];
}

/**
 * Shared condensed header for gen-3 project detail pages (OR-6123 F9): a
 * breadcrumb trail, the page title (+ optional right actions), and a single
 * meta row of `{ icon, label, value }` items — all at the 24px project
 * gutter. Replaces the per-page bespoke headers (cohort / AV / concept group
 * / dataset) so every detail surface reads consistently while keeping the new
 * condensed height.
 *
 * Composes {@link SubPageBreadcrumbHeader} for the breadcrumb + title row and
 * renders the meta row directly beneath it at the same gutter.
 */
export const ProjectDetailHeader = ({
  id,
  breadcrumbs,
  title,
  isTitleLoading,
  titleSlot,
  rightContent,
  subtitle,
  metaItems,
}: ProjectDetailHeaderProps) => {
  const hasMeta = Boolean(metaItems && metaItems.length > 0);
  const hasBreadcrumbs = Boolean(breadcrumbs && breadcrumbs.length > 0);
  const hasTitleRow = Boolean(titleSlot || title !== undefined || isTitleLoading || rightContent);

  return (
    <Box data-testid={`${id}-detail-header`}>
      {hasBreadcrumbs ? (
        <SubPageBreadcrumbHeader
          id={id}
          breadcrumbs={breadcrumbs ?? []}
          title={title}
          isTitleLoading={isTitleLoading}
          titleSlot={titleSlot}
          rightContent={rightContent}
        />
      ) : hasTitleRow ? (
        // No breadcrumb (modal host or flag-off route) — render just the title
        // row at the same gutter the breadcrumb header would use.
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            rowGap: 1,
            paddingX: 6,
            paddingTop: 2,
            paddingBottom: 1.5,
          }}
        >
          {titleSlot ??
            (isTitleLoading ? (
              <Skeleton
                variant="text"
                width={240}
                height={36}
                data-testid={`${id}-title-loading`}
              />
            ) : title !== undefined ? (
              <Typography
                variant="h5"
                component="h1"
                data-testid={`${id}-title`}
                sx={{ fontWeight: 400, minWidth: 0, overflowWrap: 'anywhere' }}
              >
                {title}
              </Typography>
            ) : (
              <span />
            ))}
          {rightContent ? (
            <Box
              data-testid={`${id}-actions`}
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
            >
              {rightContent}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {subtitle ? (
        <Box
          data-testid={`${id}-subtitle`}
          sx={{
            // Same 24px gutter as the title and meta row, pulled up so it sits
            // tight under the title (the breadcrumb header owns the bottom
            // padding above).
            paddingX: 6,
            paddingBottom: 1,
            marginTop: -1,
          }}
        >
          {typeof subtitle === 'string' ? (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          ) : (
            subtitle
          )}
        </Box>
      ) : null}
      {hasMeta ? (
        <Stack
          direction="row"
          data-testid={`${id}-meta`}
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            // 24px between meta items per the detail-header Figma spec
            // (M4 cohort visual audit; same META DATA row across artifact types).
            gap: 6,
            rowGap: 1,
            // Same 24px gutter as the breadcrumb/title above; pulled up so the
            // meta row sits tight under the title (the breadcrumb header owns
            // the bottom padding).
            paddingX: 6,
            paddingBottom: 1.5,
            marginTop: -0.5,
          }}
        >
          {metaItems?.map((item) => (
            <Box
              key={item.key}
              data-testid={`${id}-meta-${item.key}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}
            >
              {item.icon}
              {item.label ? (
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {item.label}
                </Typography>
              ) : null}
              {typeof item.value === 'string' ? (
                <Typography variant="body2" color="text.primary" noWrap>
                  {item.value}
                </Typography>
              ) : (
                item.value
              )}
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
};
